import { useEffect, useState, useRef } from 'react';
import {
  formatISO,
  getDate,
  getDaysInMonth,
  getMonth,
  getYear,
  isValid,
  parseISO,
} from 'date-fns';
import { css } from '@linaria/core';
import * as yup from 'yup';
import { getExistingFilterDataFirstValue } from '../../utils';
import Button from '../Button';
import FormHint from '../FormHint';
import FormFieldset from '../FormFieldset';
import FormLegend from '../FormLegend';
import FormField from '../FormField';
import FormLabel from '../FormLabel';
import FormTextInput from '../FormTextInput';
import CheckIcon from '../../icons/Check';
import FormError from '../FormError';

const field = css`
  margin-bottom: 0;

  ${FormLegend} {
    margin-bottom: 5px;
  }
`;

const childFields = css`
  align-items: flex-end;
  display: flex;
  gap: 10px;
  margin-bottom: 10px;

  ${FormField} {
    margin-bottom: 0;
  }

  ${FormLabel} {
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    margin-bottom: 5px;
  }
`;

const applyButton = css`
  margin-top: 10px;
  width: 100%;
`;

export default function DateRangeFilter(props) {
  const fromFieldId = props.fromFieldId;
  const toFieldId = props.toFieldId;
  const filters = props.filters;

  const hasLoadedInitialState = useRef(false);
  const prevFilters = useRef(filters);

  const [fieldData, setFieldData] = useState({
    fromDateDay: '',
    fromDateMonth: '',
    fromDateYear: '',
    toDateDay: '',
    toDateMonth: '',
    toDateYear: '',
  });

  const [errors, setErrors] = useState({ from: null, to: null });

  const validationSchema = yup
    .object({
      fromDateDay: yup.string(),
      fromDateMonth: yup.string(),
      fromDateYear: yup.string(),
      toDateDay: yup.string(),
      toDateMonth: yup.string(),
      toDateYear: yup.string(),
    })
    .test('date-validation', '', function (values) {
      const validationErrors = { from: null, to: null };

      // Validate 'from' fields
      const fromDay = values.fromDateDay
        ? parseInt(values.fromDateDay, 10)
        : null;
      const fromMonth = values.fromDateMonth
        ? parseInt(values.fromDateMonth, 10)
        : null;
      const fromYear = values.fromDateYear
        ? parseInt(values.fromDateYear, 10)
        : null;

      if ((fromDay || fromMonth) && !fromYear) {
        validationErrors.from = 'Year is required';
      } else if (fromMonth && fromMonth > 12) {
        validationErrors.from = 'Month must be 12 or less';
      } else if (fromDay && fromDay > 31) {
        validationErrors.from = 'Day must be 31 or less';
      } else if (fromDay && fromMonth && fromYear) {
        const fromDate = new Date(fromYear, fromMonth - 1, fromDay);

        if (!isValid(fromDate) || fromDate.getDate() !== fromDay) {
          const maxDays = getDaysInMonth(new Date(fromYear, fromMonth - 1));
          validationErrors.from = `Day must be ${maxDays} or less for this month`;
        }
      }

      // Validate 'to' fields
      const toDay = values.toDateDay ? parseInt(values.toDateDay, 10) : null;
      const toMonth = values.toDateMonth
        ? parseInt(values.toDateMonth, 10)
        : null;
      const toYear = values.toDateYear ? parseInt(values.toDateYear, 10) : null;

      if ((toDay || toMonth) && !toYear) {
        validationErrors.to = 'Year is required';
      } else if (toMonth && toMonth > 12) {
        validationErrors.to = 'Month must be 12 or less';
      } else if (toDay && toDay > 31) {
        validationErrors.to = 'Day must be 31 or less';
      } else if (toDay && toMonth && toYear) {
        const toDate = new Date(toYear, toMonth - 1, toDay);

        if (!isValid(toDate) || toDate.getDate() !== toDay) {
          const maxDays = getDaysInMonth(new Date(toYear, toMonth - 1));
          validationErrors.to = `Day must be ${maxDays} or less for this month`;
        }
      }

      // Combined validation: 'from' must be before 'to' if both have at least years
      if (
        !validationErrors.from &&
        !validationErrors.to &&
        fromYear &&
        toYear
      ) {
        const fromDateObj = new Date(
          fromYear,
          (fromMonth || 1) - 1,
          fromDay || 1,
        );
        const toMonthNum = toMonth || 12;
        const toYearForDays = new Date(toYear, toMonthNum - 1);
        const toDateObj = new Date(
          toYear,
          toMonthNum - 1,
          toDay || getDaysInMonth(toYearForDays),
        );

        if (fromDateObj > toDateObj) {
          validationErrors.from =
            "The 'from' date must be before the 'to' date";
        }
      }

      if (validationErrors.from || validationErrors.to) {
        return this.createError({
          message: JSON.stringify(validationErrors),
        });
      }

      return true;
    });

  useEffect(() => {
    if (prevFilters.current === filters && hasLoadedInitialState.current) {
      return;
    }

    prevFilters.current = filters;

    const fromFieldValue = getExistingFilterDataFirstValue(
      fromFieldId,
      filters,
    );
    const toFieldValue = getExistingFilterDataFirstValue(toFieldId, filters);

    if (hasLoadedInitialState.current) {
      setFieldData(current => {
        const updatedData = { ...current };
        let hasChanges = false;

        if (!fromFieldValue && current.fromDateYear !== '') {
          updatedData.fromDateDay = '';
          updatedData.fromDateMonth = '';
          updatedData.fromDateYear = '';
          hasChanges = true;
        }

        if (!toFieldValue && current.toDateYear !== '') {
          updatedData.toDateDay = '';
          updatedData.toDateMonth = '';
          updatedData.toDateYear = '';
          hasChanges = true;
        }

        return hasChanges ? updatedData : current;
      });
    } else {
      const newData = {
        fromDateDay: '',
        fromDateMonth: '',
        fromDateYear: '',
        toDateDay: '',
        toDateMonth: '',
        toDateYear: '',
      };

      if (fromFieldValue) {
        try {
          const fromDate = parseISO(fromFieldValue);
          newData.fromDateDay = String(getDate(fromDate));
          newData.fromDateMonth = String(getMonth(fromDate) + 1);
          newData.fromDateYear = String(getYear(fromDate));
        } catch (e) {
          /* empty */
        }
      }

      if (toFieldValue) {
        try {
          const toDate = parseISO(toFieldValue);
          newData.toDateDay = String(getDate(toDate));
          newData.toDateMonth = String(getMonth(toDate) + 1);
          newData.toDateYear = String(getYear(toDate));
        } catch (e) {
          /* empty */
        }
      }

      setFieldData(newData);
      hasLoadedInitialState.current = true;
    }
  }, [filters, fromFieldId, toFieldId]);

  const handleApply = () => {
    try {
      validationSchema.validateSync(fieldData);
      setErrors({ from: null, to: null });
    } catch (err) {
      const parsedErrors = JSON.parse(err.message);
      setErrors(parsedErrors);

      return;
    }

    let fromValue = null;
    let toValue = null;
    const updatedFieldData = {};

    if (fieldData.fromDateYear !== '') {
      const day = fieldData.fromDateDay || '1';
      const month = fieldData.fromDateMonth || '1';

      if (!fieldData.fromDateDay) {
        updatedFieldData.fromDateDay = day;
      }

      if (!fieldData.fromDateMonth) {
        updatedFieldData.fromDateMonth = month;
      }

      try {
        const parsedFromDate = parseISO(
          `${String(fieldData.fromDateYear).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
        );
        fromValue = formatISO(parsedFromDate);
      } catch (e) {
        /* empty */
      }
    }

    if (fieldData.toDateYear !== '') {
      const month = fieldData.toDateMonth || '12';
      const monthNum = parseInt(month, 10);
      const yearNum = parseInt(fieldData.toDateYear, 10);
      const day =
        fieldData.toDateDay ||
        String(getDaysInMonth(new Date(yearNum, monthNum - 1)));

      if (!fieldData.toDateDay) {
        updatedFieldData.toDateDay = day;
      }

      if (!fieldData.toDateMonth) {
        updatedFieldData.toDateMonth = month;
      }

      try {
        const parsedToDate = parseISO(
          `${String(fieldData.toDateYear).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T23:59:59.999Z`,
        );
        toValue = formatISO(parsedToDate);
      } catch (e) {
        /* empty */
      }
    }

    if (Object.keys(updatedFieldData).length > 0) {
      setFieldData(current => ({ ...current, ...updatedFieldData }));
    }

    const existingFromFilter = props.filters.find(f => f.field === fromFieldId);
    const existingToFilter = props.filters.find(f => f.field === toFieldId);

    if (fromValue) {
      props.setFilter(fromFieldId, fromValue);
    } else if (existingFromFilter) {
      props.removeFilter(fromFieldId);
    }

    if (toValue) {
      props.setFilter(toFieldId, toValue);
    } else if (existingToFilter) {
      props.removeFilter(toFieldId);
    }
  };

  const handleChange = (field, value) => {
    setFieldData(current => ({
      ...current,
      [field]: value,
    }));
  };

  return (
    <FormFieldset
      aria-describedby={props.formHint ? `${props.id}-hint` : undefined}
    >
      <FormLegend hasHint={props.formHint}>{props.label}</FormLegend>

      {props.formHint && (
        <FormHint id={`${props.id}-hint`}>{props.formHint}</FormHint>
      )}

      <FormFieldset className={field} hasError={!!errors.from}>
        <FormLegend>From:</FormLegend>
        {errors.from && <FormError>{errors.from}</FormError>}

        <div className={childFields}>
          <FormField>
            <FormLabel htmlFor={`${props.id}-from-day`}>Day</FormLabel>
            <FormTextInput
              id={`${props.id}-from-day`}
              inputMode="numeric"
              max={2}
              name={`${props.id}-from-day`}
              onChange={e => handleChange('fromDateDay', e.target.value)}
              type="text"
              value={fieldData.fromDateDay}
            />
          </FormField>
          <FormField>
            <FormLabel htmlFor={`${props.id}-from-month`}>Month</FormLabel>
            <FormTextInput
              id={`${props.id}-from-month`}
              inputMode="numeric"
              max={2}
              name={`${props.id}-from-month`}
              onChange={e => handleChange('fromDateMonth', e.target.value)}
              type="text"
              value={fieldData.fromDateMonth}
            />
          </FormField>
          <FormField>
            <FormLabel htmlFor={`${props.id}-from-year`}>Year</FormLabel>
            <FormTextInput
              id={`${props.id}-from-year`}
              inputMode="numeric"
              max={4}
              name={`${props.id}-from-year`}
              onChange={e => handleChange('fromDateYear', e.target.value)}
              type="text"
              value={fieldData.fromDateYear}
            />
          </FormField>
        </div>
      </FormFieldset>
      <FormFieldset className={field} hasError={!!errors.to}>
        <FormLegend>To:</FormLegend>
        {errors.to && <FormError>{errors.to}</FormError>}

        <div className={childFields}>
          <FormField>
            <FormLabel htmlFor={`${props.id}-to-day`}>Day</FormLabel>
            <FormTextInput
              id={`${props.id}-to-day`}
              inputMode="numeric"
              max={2}
              name={`${props.id}-to-day`}
              onChange={e => handleChange('toDateDay', e.target.value)}
              type="text"
              value={fieldData.toDateDay}
            />
          </FormField>
          <FormField>
            <FormLabel htmlFor={`${props.id}-to-month`}>Month</FormLabel>
            <FormTextInput
              id={`${props.id}-to-month`}
              inputMode="numeric"
              max={2}
              name={`${props.id}-to-month`}
              onChange={e => handleChange('toDateMonth', e.target.value)}
              type="text"
              value={fieldData.toDateMonth}
            />
          </FormField>
          <FormField>
            <FormLabel htmlFor={`${props.id}-to-year`}>Year</FormLabel>
            <FormTextInput
              id={`${props.id}-to-year`}
              inputMode="numeric"
              max={4}
              name={`${props.id}-to-year`}
              onChange={e => handleChange('toDateYear', e.target.value)}
              type="text"
              value={fieldData.toDateYear}
            />
          </FormField>
        </div>
      </FormFieldset>
      <Button
        StartIcon={CheckIcon}
        className={applyButton}
        disabled={
          !fieldData.fromDateDay &&
          !fieldData.fromDateMonth &&
          !fieldData.fromDateYear &&
          !fieldData.toDateDay &&
          !fieldData.toDateMonth &&
          !fieldData.toDateYear
        }
        onClick={handleApply}
        size="small"
        variant="neutral"
      >
        Apply date range
      </Button>
    </FormFieldset>
  );
}
